import { v4 } from 'uuid';
import { Workflow } from '../api/models/Workflows';

// Template steps use stable IDs so the canvas can render them before a workflow
// is copied. The graph keeps those same IDs; the copy flow remaps them to the
// newly-created steps before persisting it.
const withLinearV2Graph = (workflow: Workflow): Workflow => {
    const steps = [...workflow.steps].sort((left, right) => left.order - right.order);
    const entryStep = steps[0];

    return {
        ...workflow,
        graph: {
            nodes: steps.map((step) => ({
                id: step.id!,
                legacyStepId: step.id,
                type: step.stepType,
                config: {},
            })),
            edges: steps.slice(0, -1).map((step, index) => ({
                id: v4(),
                sourceNodeId: step.id!,
                targetNodeId: steps[index + 1].id!,
                outcome: step.stepType === 'decision' ? 'true' : 'success',
                inputName: 'input',
            })),
            triggers: entryStep ? [{
                id: v4(),
                entryNodeId: entryStep.id!,
                type: entryStep.stepType === 'schedule' ? 'schedule' : 's3',
                config: entryStep.stepType === 'data-source' ? entryStep.dataSourceStep ?? {} : {},
                enabled: workflow.enabled,
            }] : [],
        },
    };
};

export const EarningsCallTariffImpactWorkflow: Workflow = withLinearV2Graph({
    "enabled": true,
    "executionLogs": [],
    "id": "wf_template_1",
    "name": "Tariff Mentions on Earnings Call",
    "steps": [
        {
            "dataSourceStep": {
                "subType": "data-source-transcript",
                "ticker": "all"
            },
            "id": "019640d2-797d-7000-9453-f9f7857f46a8",
            "order": 1,
            "status": "success",
            "stepType": "data-source"
        },
        {
            "id": "019640d2-ac3f-7884-9fd2-2d5ed657d50a",
            "llmStep": {
                "model": "gpt-4.1",
                "prompt": "What was the expected impact of tariffs on the EPS of the company? Quote any mentions of tariffs, layoffs, or earnings impacts.",
                "useSystemPrompt": true
            },
            "order": 2,
            "status": "success",
            "stepType": "llm"
        },
        {
            "id": "019640d2-e4c4-7e27-949c-5bc44b0b2d8e",
            "order": 3,
            "emailStep": {
                "subject": "Earnings Call Tariff Impact Analysis",
                "autoTransformBody": true,
            },
            "status": "success",
            "stepType": "email"
        }
    ]
});

export const TSLADeliveriesNumbersExtractorWorkflow: Workflow = withLinearV2Graph({
    "enabled": false,
    "executionLogs": [],
    "id": "wf_template_2",
    "name": "TSLA Deliveries Extractor",
    "steps": [
        {
            "dataSourceStep": {
                "subType": "data-source-8k",
                "ticker": "all"
            },
            "id": "01965ec8-3323-7dbf-93dc-98455d1dc055",
            "lastOutput": "",
            "order": 1,
            "status": "idle",
            "stepType": "data-source"
        },
        {
            "decisionStep": {
                "attributeType": "string",
                "isSmartDecisionStep": true,
                "targetAttribute": "",
                "model": "gpt-4.1",
                "operator": "equal",
                "smartDecisionStepQuestion": "Is this a Tesla deliveries announcement?Tesla delivery announcements look like this:\"Tesla First Quarter 2025 Production, Deliveries & Deployments In the first quarter, we produced over 362,000 vehicles, delivered over 336,000 vehicles and deployed 10.4 GWh of energy storage products.While the changeover of Model Y lines across all four of our factories led to the loss of several weeks of production in Q1, the ramp of the New Model Y continues to go well.Thank you to all our customers, employees, suppliers, shareholders and supporters who helped us achieve these results.\"",
                "targetValue": ""
            },
            "id": "01965ec9-85e4-7b42-b4ae-8a604e206928",
            "lastOutput": "",
            "order": 2,
            "status": "idle",
            "stepType": "decision"
        },
        {
            "id": "01965eca-61c7-7e30-a209-a5e9b43127a5",
            "lastOutput": "",
            "llmStep": {
                "model": "gpt-4.1",
                "prompt": "Extract the deliveries numbers and then use the web search tool to determine if they beat estimates",
                "useSystemPrompt": true,
                "structuredOutput": "{\n    \"name\": \"delivery_production_counts\",\n    \"type\": \"object\",\n    \"properties\": {\n        \"total_deliveries_count\": {\n            \"type\": \"integer\",\n            \"description\": \"The total number of deliveries made.\"\n        },\n        \"total_production_count\": {\n            \"type\": \"integer\",\n            \"description\": \"The total number of products produced.\"\n        },\n        \"beat_estimates\": {\n            \"type\": \"boolean\",\n            \"description\": \"Whether or not the deliveries numbers beat estimates gather from the web_search tool\"\n        }\n    },\n    \"required\": [\n        \"total_deliveries_count\",\n        \"total_production_count\"\n    ],\n    \"additionalProperties\": false\n}"
            },
            "order": 3,
            "status": "idle",
            "stepType": "llm"
        },
        {
            "apiDestinationStep": {
                "authHeaderName": "Authorization",
                "authHeaderSecretName": "My Service Secret",
                "body": "null",
                "headers": {},
                "method": "POST",
                "url": "https://my-trading-service.com/v1/strategies/tsla-deliveries"
            },
            "id": "01965ecb-b8e4-720f-803e-e2edcd2516c1",
            "lastOutput": "",
            "order": 4,
            "status": "idle",
            "stepType": "api-destination"
        }
    ]
});

export const NewTarrifMarketImpactAnalysisWorkflow: Workflow = withLinearV2Graph({
    "enabled": false,
    "executionLogs": [],
    "id": "wf_template_3",
    "name": "Market Impact of Executive Order",
    "steps": [
        {
            "dataSourceStep": {
                "subType": "data-source-usa-executive-order",
                "ticker": "all"
            },
            "id": "01965ed2-711d-725d-8b2f-0365e19ccce8",
            "lastOutput": "",
            "order": 1,
            "status": "idle",
            "stepType": "data-source"
        },
        {
            "decisionStep": {
                "attributeType": "string",
                "targetAttribute": "",
                "isSmartDecisionStep": true,
                "model": "gpt-4.1",
                "operator": "equal",
                "smartDecisionStepQuestion": "Is this executive order announcing new tariffs?",
                "targetValue": ""
            },
            "id": "01965ed2-b4e3-7d17-b5af-e7009eea5b3e",
            "lastOutput": "",
            "order": 2,
            "status": "idle",
            "stepType": "decision"
        },
        {
            "id": "01965ed2-ed6c-7582-b383-824e2b7c425c",
            "lastOutput": "",
            "llmStep": {
                "model": "o3-mini",
                "prompt": "Use the web search tool to find the 5 public companies most likely to be impacted by these tariffs then use the your tools to estimate to the impact to revenue and net income for each of these companies as a result of these new tariffs",
                "reasoningEffort": "high",
                "useSystemPrompt": true
            },
            "order": 3,
            "status": "idle",
            "stepType": "llm"
        },
        {
            "emailStep": {
                "subject": "Workflow Execution: Market Impact of Tariff EO",
                "autoTransformBody": true,
            },
            "id": "01965ed4-9362-735b-a25c-4c50490fe088",
            "lastOutput": "",
            "order": 4,
            "status": "idle",
            "stepType": "email"
        }
    ]
});

export const FedAnnouncemntsWorkflow: Workflow = withLinearV2Graph({
    "enabled": false,
    "executionLogs": [],
    "id": "wf_template_4",
    "name": "Goodwill Audit",
    "steps": [
        {
            "dataSourceStep": {
                "subType": "data-source-8k-earnings",
                "ticker": "all"
            },
            "id": "0195d8d4-8244-77df-a466-3a48dd253e06",
            "order": 1,
            "status": "success",
            "stepType": "data-source"
        },
        {
            "decisionStep": {
                "attributeType": "string",
                "targetAttribute": "",
                "isSmartDecisionStep": true,
                "model": "gpt-4.1",
                "operator": "equal",
                "smartDecisionStepQuestion": "Is goodwill more than 50% of assets as of this report when it wasn't in the past.",
                "targetValue": ""
            },
            "id": "0195d8d4-c55a-7ac4-877b-429d714e61d4",
            "lastOutput": "false",
            "order": 2,
            "status": "success",
            "stepType": "decision"
        },
        {
            "id": "0195d8d5-15cb-7dd2-a3bd-a3ae91be2995",
            "lastOutput": "",
            "llmStep": {
                "model": "o3-mini",
                "prompt": "Write a short thesis about how this 8-K is the turning point with regards to the companies high percentage of goodwill to total assets and how that makes this company over valued. Use the web search tool to obtain the current market value of the company. Use information about the company's acquisitions and historical balance sheet values to inform your thesis. Include markdown citations to all your sources. At the top of the post include a disclaimer \"This output is generated by a workflow run on [PocketQuant](https://pocket-quant.com)\"",
                "reasoningEffort": "high",
                "structuredOutput": "{\n    \"name\": \"post\",\n    \"type\": \"object\",\n    \"properties\": {\n        \"title\": {\n            \"type\": \"string\",\n            \"description\": \"The title of the post.\"\n        },\n        \"content\": {\n            \"type\": \"string\",\n            \"description\": \"The content of the post.\"\n        }\n    },\n    \"required\": [\n        \"title\",\n        \"content\"\n    ],\n    \"additionalProperties\": false\n}",
                "useSystemPrompt": true
            },
            "order": 3,
            "status": "idle",
            "stepType": "llm"
        },
        {
            "id": "019640d4-b828-7ca6-b610-65db6b87704d",
            "lastOutput": "",
            "order": 4,
            "emailStep": {
                "subject": "Workflow Execution: Goodwill Red Flags",
                "autoTransformBody": true,
            },
            "status": "idle",
            "stepType": "email"
        }
    ]
});

export const S1IPOActivityWorkflow: Workflow = withLinearV2Graph({
    "enabled": false,
    "executionLogs": [],
    "id": "wf_template_5",
    "name": "S-1 IPO Filing Alert",
    "steps": [
        {
            "dataSourceStep": {
                "subType": "data-source-s1",
                "ticker": "all"
            },
            "id": "019bf200-1111-7111-8111-111111111111",
            "order": 1,
            "status": "idle",
            "stepType": "data-source"
        },
        {
            "id": "019bf200-2222-7222-8222-222222222222",
            "llmStep": {
                "model": "gpt-4.1",
                "prompt": "An S-1 or S-1/A filing refreshed the IPO calendar. Identify the issuer with the most recent filing and write a concise alert with the company name, CIK, filing date, form type, and SEC filing link. Explain that an S-1 filing signals an IPO registration, not a confirmed IPO date or pricing.",
                "useSystemPrompt": true
            },
            "order": 2,
            "status": "idle",
            "stepType": "llm"
        },
        {
            "id": "019bf200-3333-7333-8333-333333333333",
            "order": 3,
            "emailStep": {
                "subject": "New S-1 IPO filing activity",
                "autoTransformBody": true
            },
            "status": "idle",
            "stepType": "email"
        }
    ]
});

// export const RedditSentimentAnalysisWorkflow // needs a schedule step


export const workflowTemplates: Workflow[] = [
    EarningsCallTariffImpactWorkflow,
    TSLADeliveriesNumbersExtractorWorkflow,
    NewTarrifMarketImpactAnalysisWorkflow,
    FedAnnouncemntsWorkflow,
    S1IPOActivityWorkflow
]
